/**
 * Lake Charles Prerender Script
 *
 * Used by vite-prerender-plugin to generate static HTML at build time.
 * This enables search engines to crawl content without JavaScript execution.
 *
 * How it works:
 * 1. Plugin crawls the app starting from '/' and additionalPrerenderRoutes
 * 2. For each route, it sets globalThis.location and calls this prerender() function
 * 3. We render the React app to a string using react-dom/server
 * 4. Plugin injects the HTML into index.html at #root
 * 5. Output: fully rendered static HTML files for each route
 *
 * React Router v7 Note:
 * React Router v7 removed the StaticRouter export from react-router-dom/server.
 * Instead, we use MemoryRouter which works in Node.js environments.
 */

import React from 'react';
import { renderToString } from 'react-dom/server';
import { MemoryRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Import page components directly for SSR
import { CityLayout } from '@core';
import lakeCharlesConfig from './city.config';
import { LakeCharlesHomePage } from './pages/HomePage';
import { LakeCharlesPrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { LakeCharlesTermsOfServicePage } from './pages/TermsOfServicePage';
import { LakeCharlesVendorsPage } from './pages/VendorsPage';
import { LakeCharlesCityPartnershipPage } from './pages/CityPartnershipPage';
import { LakeCharlesChampionApplicationPage } from './pages/ChampionApplicationPage';

// Route-specific SEO metadata
const routeMeta: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Lake Charles Community App - Organize Neighborhoods & Vote | Louisiana Civic Platform',
    description:
      "Lake Charles, Louisiana's community organizing platform. Vote on local issues, create petitions, organize neighbors, and hold decision-makers accountable. Free for Lake Charles residents.",
  },
  '/privacy': {
    title: 'Privacy Policy | Lake Charles Community App',
    description:
      'Privacy policy for the Lake Charles Community App. Learn how we protect your data and respect your privacy.',
  },
  '/terms': {
    title: 'Terms of Service | Lake Charles Community App',
    description:
      'Terms of service for the Lake Charles Community App. Understand your rights and responsibilities as a user.',
  },
  '/vendors': {
    title: 'Vendor Directory | Lake Charles Community App',
    description:
      'Find local vendors and service providers in Lake Charles, Louisiana. Connect with trusted businesses in your community.',
  },
  '/city-partnership': {
    title: 'City Partnership Program | Lake Charles Community App',
    description:
      'Partner with ToolboxLogic to bring civic engagement tools to Lake Charles. Learn how city officials can leverage our platform.',
  },
  '/champions/apply': {
    title: 'Become a Community Champion | Lake Charles Community App',
    description:
      'Apply to become a Community Champion in Lake Charles. Lead your neighborhood, drive engagement, and make a difference.',
  },
};

// Default metadata for routes not explicitly defined
const defaultMeta = {
  title: 'Lake Charles Community App | ToolboxLogic',
  description: 'Your voice, amplified. The tools Lake Charles needs to organize and be heard.',
};

// Navigation and footer links (same as App.tsx)
const navLinks = [
  { label: 'Features', href: '/#features' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Vendors', href: '/vendors' },
  { label: 'City Officials', href: '/city-partnership' },
  { label: 'Champions', href: '/#champions' },
];

const footerLinks = [
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy' },
];

const socialLinks = [
  {
    platform: 'facebook' as const,
    href: 'https://www.facebook.com/groups/lakecharles.communityapp',
    label: 'Facebook Group',
  },
  {
    platform: 'instagram' as const,
    href: 'https://www.instagram.com/lakecharles.communityapp/',
    label: 'Instagram',
  },
];

/**
 * SSR-safe App component for prerendering
 * Uses MemoryRouter instead of BrowserRouter for Node.js compatibility
 */
function PrerenderApp({ url }: { url: string }) {
  return (
    <MemoryRouter initialEntries={[url]}>
      <CityLayout
        cityName={lakeCharlesConfig.identity.cityName}
        stateName={lakeCharlesConfig.identity.stateName}
        logoSrc="/media_lakecharles/lake-charles-community-app.svg"
        logoAlt="Lake Charles Community App logo"
        navLinks={navLinks}
        footerLinks={footerLinks}
        socialLinks={socialLinks}
        primaryCtaText="Join Waitlist"
        primaryCtaHref="/#interest-form"
        enableWaitlistDialog={true}
        vendorsPagePath="/vendors"
      >
        <Routes>
          <Route path="/" element={<LakeCharlesHomePage />} />
          <Route path="/privacy" element={<LakeCharlesPrivacyPolicyPage />} />
          <Route path="/terms" element={<LakeCharlesTermsOfServicePage />} />
          <Route path="/vendors" element={<LakeCharlesVendorsPage />} />
          <Route path="/city-partnership" element={<LakeCharlesCityPartnershipPage />} />
          <Route path="/champions/apply" element={<LakeCharlesChampionApplicationPage />} />
          <Route path="*" element={<LakeCharlesHomePage />} />
        </Routes>
      </CityLayout>
    </MemoryRouter>
  );
}

/**
 * Prerender function called by vite-prerender-plugin
 * @param data - Contains the URL being rendered
 * @returns HTML string and optional head elements
 */
export async function prerender(data: { url: string }) {
  const helmetContext: { helmet?: any } = {};

  const html = renderToString(
    <HelmetProvider context={helmetContext}>
      <PrerenderApp url={data.url} />
    </HelmetProvider>,
  );

  // Get route-specific or default metadata
  const meta = routeMeta[data.url] || defaultMeta;

  // Parse links from rendered HTML for auto-discovery
  const { parseLinks } = await import('vite-prerender-plugin/parse');
  const links = parseLinks(html);

  return {
    html,
    links: new Set(links),
    head: {
      lang: 'en',
      title: meta.title,
      elements: new Set([
        {
          type: 'meta',
          props: {
            name: 'description',
            content: meta.description,
          },
        },
        {
          type: 'meta',
          props: {
            property: 'og:title',
            content: meta.title,
          },
        },
        {
          type: 'meta',
          props: {
            property: 'og:description',
            content: meta.description,
          },
        },
        {
          type: 'meta',
          props: {
            property: 'og:url',
            content: `https://lakecharles.toolboxlogic.com${data.url}`,
          },
        },
        {
          type: 'link',
          props: {
            rel: 'canonical',
            href: `https://lakecharles.toolboxlogic.com${data.url}`,
          },
        },
      ]),
    },
  };
}
